/**
 * HealthBars — world-space bars above remote players.
 *
 * Two sprites per player: a dark backing and a coloured fill whose X scale is
 * the health fraction. Sprites, not DOM, because eight bars projected and
 * written to the DOM every frame is eight layout invalidations per frame; and
 * not canvas textures, because a bar that changes 30 times a second would mean
 * 30 texture uploads.
 *
 * The value drawn is always the server's. `health` comes off the snapshot, and
 * the bar has no local prediction path at all — a health bar that guesses is a
 * health bar that lies to a player with money in the pot.
 */

import * as THREE from 'three/webgpu';
import { uv, float, vec2, smoothstep, uniform } from 'three/tsl';
import { CHARACTERS, AMMO_MAX, COIN_CAP } from '@chaos/protocol';

const BAR_WIDTH = 1.5;
const BAR_HEIGHT = 0.22;
const BAR_LIFT = 2.25;

/**
 * The ammo pips, under the LOCAL player's bar only.
 *
 * Brawl Stars draws three chunky segments under the health bar and it is the
 * whole reason a magazine reads at a glance mid-fight: you never look at the
 * corner of the screen for it. Same object here, in v2's own palette — the
 * fill is --gold on --ink, never a hue that could be read as a health colour
 * or a hazard, and never purple.
 *
 * The pip being reloaded fills left-to-right with the sim's `reload`
 * fraction, so "how long until the next shot" is a length, not a guess.
 */
const PIP_WIDTH = 0.42;
const PIP_HEIGHT = 0.15;
const PIP_GAP = 0.07;
const PIP_DROP = 0.24;
const PIP_PAD = 0.06;
/** --gold from styles.css. The pips' outline is BAR_INK, the same pen as the bar's. */
const PIP_GOLD = 0xffc531;
/** Corner radius as a fraction of the pip's height. */
const PIP_ROUND = 0.42;
/** Beyond this distance the bar is hidden — it would be a 2px smear anyway. */
const MAX_VISIBLE_DISTANCE = 55;

/**
 * The backing is an OUTLINE, not a shadow.
 *
 * It used to be a 72%-opaque near-black behind the fill, padded by 0.07 — over
 * a dark platform that is nearly invisible, and the bar lost its edges exactly
 * where the arena is darkest. Opaque ink, padded enough to read as a stroke at
 * distance, is the same idea as the 3px border on every HUD element and it is
 * what makes these legible against a bright deck AND a dark wall.
 */
// Was 0x0d0520 — a violet-black, left over from v1's palette. The outline on
// every other HUD element is --ink, and these have to look like the same pen.
export const BAR_INK = 0x04080a;
const BAR_PAD_X = 0.14;
const BAR_PAD_Y = 0.12;

/* Health is a value with a direction, which is why it may use the direction
   hues at all. Two stops, not three: the midpoint used to be amber 0xffc107,
   which sits within a stop of --gold — a half-health bar reading as a payout.
   Replacing it with a third red left two stops that both read as "red" at
   distance, so the middle one stopped earning its place. These mirror --hp
   and --hp-low in styles.css. */
const HEALTHY = new THREE.Color(0x00e07a);
const CRITICAL = new THREE.Color(0xff3d5a);


export function makeSprite(colorHex, opacity) {
  const material = new THREE.SpriteNodeMaterial({ color: colorHex, transparent: true });
  material.opacity = opacity;
  material.depthTest = false;
  material.depthWrite = false;
  const sprite = new THREE.Sprite(material);
  sprite.userData.noOutline = true;
  return sprite;
}

/**
 * A rounded-rectangle sprite: the pip. The sprite is a square quad scaled to
 * `width x height`, so the rounding is computed in aspect-corrected UV space —
 * a naive circle in UV becomes an ellipse once the quad is stretched.
 *
 * `fillFrac` is a uniform: the reloading pip reveals itself left-to-right by
 * masking, not by scaling, so the corners stay round while it fills.
 */
function makePipSprite(colorHex, width, height) {
  const material = new THREE.SpriteNodeMaterial({ color: colorHex, transparent: true });
  const fillFrac = uniform(1);
  const aspect = width / height;
  const local = uv().sub(0.5).mul(2);
  const p = vec2(local.x.mul(aspect).abs(), local.y.abs());
  const r = float(PIP_ROUND * 2);
  const q = p.sub(vec2(aspect - PIP_ROUND * 2, 1 - PIP_ROUND * 2)).max(0);
  const d = q.length().sub(r);
  const inside = smoothstep(float(0.06), float(-0.06), d);
  const revealed = smoothstep(fillFrac.add(0.01), fillFrac.sub(0.01), uv().x);
  material.opacityNode = inside.mul(revealed);
  material.depthTest = false;
  material.depthWrite = false;
  material.userData.fillFrac = fillFrac;
  const sprite = new THREE.Sprite(material);
  sprite.scale.set(width, height, 1);
  sprite.userData.noOutline = true;
  return sprite;
}

/**
 * Attaches a bar to a character group. Idempotent.
 *
 * The bar is parented to the character so it inherits position for free, and
 * `renderOrder` puts it above the body — a bar that z-fights a shoulder is
 * unreadable at exactly the moment it matters.
 */
/**
 * Health bars are world-space objects parented to the character, NOT part of
 * `#hud` — so the attract page's CSS, which hides every HUD root, cannot touch
 * them. A bar floating over a character-select portrait reads as a bug, and
 * this is the only lever that reaches it.
 */
let enabled = true;

export function setHealthBarsEnabled(on) {
  enabled = on;
}

export function attachHealthBar(group, characterId) {
  if (!enabled) return;
  const existing = group.userData.healthBar;
  if (existing) {
    // A player who swaps characters between rounds keeps their mesh group; a
    // bar still dividing by the OLD maxHealth would draw the wrong fraction.
    if (existing.characterId !== characterId) {
      existing.characterId = characterId;
      existing.maxHealth = (CHARACTERS[characterId] ?? CHARACTERS.volt).maxHealth;
    }
    return existing;
  }

  const def = CHARACTERS[characterId] ?? CHARACTERS.volt;
  const holder = new THREE.Group();
  holder.position.y = BAR_LIFT;
  holder.renderOrder = 20;

  const back = makeSprite(BAR_INK, 1);
  back.scale.set(BAR_WIDTH + BAR_PAD_X, BAR_HEIGHT + BAR_PAD_Y, 1);
  back.renderOrder = 20;
  holder.add(back);

  const fill = makeSprite(def.colorHex, 1);
  fill.scale.set(BAR_WIDTH, BAR_HEIGHT, 1);
  fill.renderOrder = 21;
  holder.add(fill);

  group.add(holder);
  const bar = { holder, back, fill, characterId, maxHealth: def.maxHealth };
  group.userData.healthBar = bar;
  return bar;
}

/**
 * The coin badge: a gold chip at the bar's left end carrying the holder's
 * coin count. Brawl Stars puts the power-cube count exactly here, and it is
 * the one number that tells you who is worth hunting.
 *
 * Gold because a coin is money (the colour rule in styles.css). The digit is
 * a canvas texture, but only COIN_CAP + 1 of them ever exist and they are
 * shared by every badge, so a count changing is a material swap, not an upload.
 */
const BADGE_SIZE = 0.46;
const BADGE_GAP = 0.1;
const _badgeTextures = new Map();

function badgeTexture(count) {
  let tex = _badgeTextures.get(count);
  if (tex) return tex;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 64;
  const g = canvas.getContext('2d');
  // Ink stroke, gold face, ink digit — the chip family's bevel language.
  g.fillStyle = '#04080a';
  roundRect(g, 2, 2, 60, 60, 16);
  g.fill();
  const grad = g.createLinearGradient(0, 6, 0, 58);
  grad.addColorStop(0, '#ffe27a');
  grad.addColorStop(0.46, '#ffc531');
  grad.addColorStop(1, '#e08c12');
  g.fillStyle = grad;
  roundRect(g, 7, 7, 50, 50, 12);
  g.fill();
  g.fillStyle = '#04080a';
  g.font = '900 40px "Lilita One", system-ui, sans-serif';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillText(String(count), 32, 35);
  tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  _badgeTextures.set(count, tex);
  return tex;
}

function roundRect(g, x, y, w, h, r) {
  g.beginPath();
  g.roundRect(x, y, w, h, r);
}

/**
 * Drains a bar's fill to `frac` of `width`, pinned to the left edge, and tints
 * it critical-to-healthy. A sprite scales about its centre, so shrinking alone
 * would empty it from both ends; shifting X by half the lost width is what
 * makes it read as draining. Shared by every bar in the world — players' and
 * crates' — so they drain and colour the same way.
 */
export function setBarFill(fill, frac, width) {
  const f = Math.max(0, Math.min(1, frac));
  const w = width * f;
  fill.scale.x = Math.max(0.001, w);
  fill.position.x = -(width - w) / 2;
  // Colour carries the same information as length, which is what makes the bar
  // readable in peripheral vision during a fight.
  fill.material.color.copy(CRITICAL).lerp(HEALTHY, f);
}

/**
 * Shows `coins` beside an existing bar, or hides the badge at zero. Cheap to
 * call every frame: nothing changes unless the count does.
 */
export function updateCoinBadge(group, coins) {
  const bar = group.userData.healthBar;
  if (!bar) return;
  // Clamped, so a bad snapshot cannot mint a texture per integer.
  const count = Math.max(0, Math.min(COIN_CAP, Math.trunc(coins ?? 0)));
  if (count === 0) {
    if (bar.badge) bar.badge.visible = false;
    return;
  }
  if (typeof document === 'undefined') return;
  if (!bar.badge) {
    const material = new THREE.SpriteNodeMaterial({ transparent: true });
    material.depthTest = false;
    material.depthWrite = false;
    const badge = new THREE.Sprite(material);
    badge.scale.set(BADGE_SIZE, BADGE_SIZE, 1);
    badge.position.set(-(BAR_WIDTH + BAR_PAD_X) / 2 - BADGE_GAP - BADGE_SIZE / 2, 0, 0);
    badge.renderOrder = 22;
    badge.userData.noOutline = true;
    bar.holder.add(badge);
    bar.badge = badge;
    bar.badgeCount = 0;
  }
  bar.badge.visible = true;
  if (bar.badgeCount !== count) {
    bar.badge.material.map = badgeTexture(count);
    bar.badge.material.needsUpdate = true;
    bar.badgeCount = count;
  }
}

export function removeHealthBar(group) {
  const bar = group.userData.healthBar;
  if (!bar) return;
  group.remove(bar.holder);
  bar.back.material.dispose();
  bar.fill.material.dispose();
  bar.badge?.material.dispose();
  if (bar.pips) {
    for (const pip of bar.pips) {
      pip.back.material.dispose();
      pip.fill.material.dispose();
    }
  }
  group.userData.healthBar = null;
}

/**
 * Adds the ammo pips under an existing bar. Idempotent, and only ever called
 * for the local player — an opponent's magazine is not something a Brawl
 * Stars player reads off their body either, and it keeps the remote bars one
 * draw call lighter each.
 */
export function attachAmmoPips(group) {
  const bar = group.userData.healthBar;
  if (!bar || bar.pips) return bar?.pips ?? null;
  const pips = [];
  const total = AMMO_MAX * PIP_WIDTH + (AMMO_MAX - 1) * PIP_GAP;
  for (let i = 0; i < AMMO_MAX; i++) {
    const x = -total / 2 + PIP_WIDTH / 2 + i * (PIP_WIDTH + PIP_GAP);
    const back = makePipSprite(BAR_INK, PIP_WIDTH + PIP_PAD, PIP_HEIGHT + PIP_PAD);
    back.position.set(x, -PIP_DROP, 0);
    back.renderOrder = 20;
    const fill = makePipSprite(PIP_GOLD, PIP_WIDTH, PIP_HEIGHT);
    fill.position.set(x, -PIP_DROP, 0);
    fill.renderOrder = 21;
    bar.holder.add(back, fill);
    pips.push({ back, fill });
  }
  bar.pips = pips;
  return pips;
}

/**
 * @param {THREE.Group} group the local body
 * @param {number} ammo whole pips in the magazine
 * @param {number} reload 0..1 progress toward the next pip
 */
export function updateAmmoPips(group, ammo, reload) {
  const bar = group.userData.healthBar;
  if (!bar?.pips) return;
  for (let i = 0; i < bar.pips.length; i++) {
    const { fill } = bar.pips[i];
    const frac = i < ammo ? 1 : i === ammo ? Math.max(0, Math.min(1, reload)) : 0;
    fill.material.userData.fillFrac.value = frac;
    fill.visible = frac > 0.001;
  }
}

/**
 * Updates one bar.
 *
 * A sprite scales about its centre, so shrinking the fill alone would empty it
 * from both ends. Shifting X by half the lost width pins it to the left edge,
 * which is the only way a bar reads as draining rather than closing.
 */
export function updateHealthBar(group, health, cameraPosition) {
  const bar = group.userData.healthBar;
  if (!bar) return;

  const distance = cameraPosition.distanceTo(group.position);
  const visible = distance < MAX_VISIBLE_DISTANCE && health > 0;
  bar.holder.visible = visible;
  if (!visible) return;

  // UNDO THE BODY'S ROTATION FIRST.
  //
  // The bar is parented to the character group so it inherits position for
  // free — but that group is turned to face the player's aim, and the drain
  // offset below is a translation along the holder's local X. Inheriting the
  // yaw meant that offset swung around the body: facing one way the bar drained
  // from the left, facing another it slid right, and edge-on it vanished into
  // the screen and the bar looked centred. Reported as the bar being "out of
  // place, displaced" — which is exactly what it was doing, once per rotation.
  //
  // The camera is fixed at yaw 0, so cancelling the parent's yaw leaves local
  // +X pointing at screen-right, which is what "the left edge of the bar" has
  // to mean. `StatusAura` cancels it for the same reason.
  bar.holder.rotation.y = -group.rotation.y;

  setBarFill(bar.fill, health / bar.maxHealth, BAR_WIDTH);

  // Bars shrink with distance more slowly than perspective would, so a distant
  // opponent's health is still legible without dominating a nearby one.
  const scale = 1 + Math.min(distance / MAX_VISIBLE_DISTANCE, 1) * 0.9;
  bar.holder.scale.setScalar(scale);
}
